import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FiLock, FiShield } from 'react-icons/fi';
import { auth } from '../config/supabase';

// Reached when a user clicks the password-recovery link in their email.
// The Supabase JS client auto-detects the access token in the URL hash on load
// and emits a `PASSWORD_RECOVERY` event, at that point we know it's safe to
// call `auth.updatePassword()` for the current session.
const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasRecoverySession, setHasRecoverySession] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // If we already have a session at mount, the token was processed already.
    auth.getSession().then(({ data }) => {
      if (data?.session) setHasRecoverySession(true);
    });

    // Also listen for the PASSWORD_RECOVERY event in case the token is still
    // being processed when this component mounts.
    const unsubscribe = auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        setHasRecoverySession(true);
        setError(null);
      }
    });

    return unsubscribe;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!hasRecoverySession) {
      setError(
        'No password recovery session detected. Please open the most recent password reset link from your email.'
      );
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await auth.updatePassword(password);
      if (updateError) {
        setError(updateError.message || 'Failed to update password.');
        toast.error('Could not update password');
        return;
      }

      // Don't dump the user into the dashboard if their account isn't an
      // approved admin, otherwise the recovery flow bypasses the approval
      // gate enforced inside signIn().
      const isAdmin = await auth.isAdmin();
      if (!isAdmin) {
        await auth.signOut();
        setError('Password updated, but this account does not have admin access. Sign in again once your registration is approved.');
        toast.error('No admin access on this account');
        return;
      }

      setSuccess(true);
      toast.success('Password updated');
      setTimeout(() => navigate('/admin-dashboard'), 1200);
    } catch (err) {
      setError(err.message || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white p-4">
      <div className="bg-[#121212] rounded-xl shadow-lg border border-white/20 max-w-md w-full p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-3">
            <FiShield className="text-white text-3xl" />
          </div>
          <h1 className="text-2xl font-bold text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            Set a New Password
          </h1>
          <p className="text-gray-400 text-sm mt-1 text-center">
            Choose a new password for your admin account.
          </p>
        </div>

        {!hasRecoverySession && !success && (
          <div className="bg-yellow-500/10 border border-yellow-500/40 text-yellow-200 text-sm p-3 rounded mb-4">
            Waiting for your recovery link to be verified. If this message persists, the link may have expired, request a new one from the login page.
          </div>
        )}

        {error && (
          <div className="bg-red-500/15 border border-red-500/40 text-red-200 text-sm p-3 rounded mb-4">
            {error}
          </div>
        )}

        {success ? (
          <div className="bg-green-500/15 border border-green-500/40 text-green-200 text-sm p-3 rounded">
            Password updated. Redirecting to the admin dashboard…
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">New password</label>
              <div className="relative">
                <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={!hasRecoverySession || loading}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="w-full bg-black border border-gray-700 text-white pl-10 pr-3 py-2 rounded focus:outline-none focus:border-white/40"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Confirm new password</label>
              <div className="relative">
                <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={!hasRecoverySession || loading}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="w-full bg-black border border-gray-700 text-white pl-10 pr-3 py-2 rounded focus:outline-none focus:border-white/40"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={!hasRecoverySession || loading}
              className="w-full bg-white text-black py-2.5 rounded font-medium disabled:opacity-50 hover:bg-gray-100 transition"
            >
              {loading ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
